import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { authFetch } from "./authFetch";

interface RouterContextType {
  path: string;
  navigate: (to: string) => void;
  unreadDocCount: number;
  refreshUnreadCount: () => void;
  unreadNotifCount: number;
  refreshNotifCount: () => void;
}

const RouterContext = createContext<RouterContextType>({
  path: "/",
  navigate: () => {},
  unreadDocCount: 0,
  refreshUnreadCount: () => {},
  unreadNotifCount: 0,
  refreshNotifCount: () => {},
});

export function PortailRouter({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname || "/");
  const [unreadDocCount, setUnreadDocCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  // Handle browser back/forward
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname || "/");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    if (window.location.pathname !== to) {
      window.history.pushState(null, "", to);
    }
    setPath(to);
  }, []);

  const refreshUnreadCount = useCallback(() => {
    authFetch("/api/portail/notifications/unread-count")
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(data => setUnreadDocCount(data.count || 0))
      .catch(() => setUnreadDocCount(0));
  }, []);

  const refreshNotifCount = useCallback(() => {
    authFetch("/api/portail/notifications")
      .then(r => r.ok ? r.json() : { unreadCount: 0 })
      .then(data => setUnreadNotifCount(data.unreadCount || 0))
      .catch(() => setUnreadNotifCount(0));
  }, []);

  // Fetch on mount
  useEffect(() => {
    if (path === "/auth/callback" || path === "/reset-password") return;
    const t = setTimeout(() => {
      refreshUnreadCount();
      refreshNotifCount();
    }, 1500);
    return () => clearTimeout(t);
  }, [path, refreshUnreadCount, refreshNotifCount]);

  // Poll notifications every 30s
  useEffect(() => {
    if (path === "/auth/callback" || path === "/reset-password") return;
    const interval = setInterval(refreshNotifCount, 30000);
    return () => clearInterval(interval);
  }, [path, refreshNotifCount]);

  return (
    <RouterContext.Provider value={{ path, navigate, unreadDocCount, refreshUnreadCount, unreadNotifCount, refreshNotifCount }}>
      {children}
    </RouterContext.Provider>
  );
}

export function usePortailRouter() {
  return useContext(RouterContext);
}

interface LinkProps {
  to: string;
  className?: string;
  children: ReactNode;
  title?: string;
  style?: React.CSSProperties;
}

export function PortailLink({ to, className, children, title, style }: LinkProps) {
  const { navigate } = usePortailRouter();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
  };

  return (
    <a
      href={to}
      onClick={handleClick}
      className={className}
      title={title}
      style={style}
    >
      {children}
    </a>
  );
}
